import { useEffect, useMemo, useState } from 'react'
import { PartnerRepository } from '../data/partnerRepository'
import type { PartnerInvitePreview } from '../domain/partner'
import { useAuthSession } from '../auth/AuthSessionContext'

export function PartnerInviteEntry({ token }: { token: string }) {
  const repository = useMemo(() => new PartnerRepository(), [])
  const { mode, signIn, registerPartner } = useAuthSession()
  const [preview, setPreview] = useState<PartnerInvitePreview | null>(null)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')

  useEffect(() => {
    if (mode === 'prototype') {
      setPreview({ active: true, enrolmentAvailable: true, expiresAt: null, notice: 'Prototype partner preview. No live relationship data is created.' })
      return
    }
    void repository.getInvitePreview(token).then(setPreview).catch(() => setMessage('This invitation could not be verified. No partner profile was created.'))
  }, [mode, repository, token])

  async function enter(kind: 'signin' | 'create') {
    setBusy(true)
    setMessage('')
    try {
      if (mode === 'prototype') await signIn(email, password, 'partner')
      else if (kind === 'signin') await signIn(email, password)
      else {
        await registerPartner(email, password)
        setMessage('Account created. Verify the email we sent, then return to this invitation and sign in. Nothing is shared until you choose it.')
      }
    } catch {
      setMessage(kind === 'create' ? 'The account could not be created. Check the email and password, or sign in if this account already exists.' : 'Sign-in was not accepted. Use the exact email address that received the invitation.')
    } finally { setBusy(false) }
  }

  if (!preview) return <main className="partner-entry-shell"><section className="partner-entry-card"><p>Checking this private invitation...</p>{message && <p role="alert">{message}</p>}</section></main>
  if (!preview.enrolmentAvailable || !preview.active) return <main className="partner-entry-shell"><section className="partner-entry-card"><p className="eyebrow">Partner space</p><h1>This invitation is not available.</h1><p>{preview.notice}</p><p>No profile has been created and no explanation is required.</p></section></main>

  return <main className="partner-entry-shell">
    <section className="partner-entry-card" aria-labelledby="partner-entry-title">
      <div className="signin-brand"><span>PM</span><p>Primal<br /><strong>Partner Space</strong></p></div>
      <p className="eyebrow">An invitation, never an obligation</p>
      <h1 id="partner-entry-title">Your voice stays yours.</h1>
      <p>{preview.notice}</p>
      <div className="partner-boundary-note"><strong>Before you enter</strong><p>The man cannot read this private workspace. You preview every item that may be shared. You can change consent, unlink, export or delete without giving a reason.</p></div>
      <label><span>Email that received the invitation</span><input type="email" autoComplete="username" value={email} onChange={(event) => setEmail(event.target.value)} /></label>
      {mode === 'firebase' && <label><span>Password</span><input type="password" minLength={12} autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} /></label>}
      {message && <p className="partner-message" role="status">{message}</p>}
      <div className="partner-entry-actions">
        <button type="button" disabled={busy || !email || (mode === 'firebase' && password.length < 12)} onClick={() => void enter('signin')}>{busy ? 'Please wait...' : 'Sign in to review'}</button>
        <button type="button" className="secondary-action" disabled={busy || !email || (mode === 'firebase' && password.length < 12)} onClick={() => void enter('create')}>Create independent account</button>
      </div>
    </section>
  </main>
}
